import { getArticle, getArticleRecommendations, SettingKey } from "@serophin/nottldr-shared";
import { useReadArticles, useSettings } from "@serophin/nottldr-shared/hooks";
import { useEffect } from "preact/hooks";
import { ArticleAside } from "../../components/article/ArticleAside";
import { ArticleBody, Summary } from "../../components/article/ArticleBody";
import { ArticleHero, Attribution, ByLine, HeaderControls } from "../../components/article/ArticleHeader";
import { ScrollProgress } from "../../components/article/ScrollProgress";
import { useAutoRead } from "../../hooks/useAutoRead";
import { useHeaderActions } from "../../hooks/useHeaderActions";
import { useIsSignedIn } from "../../platform/session";
import { titleOf } from "../../siteMeta";
import { NotFoundPage } from "../NotFoundPage";
import type { PageProps } from "../pageProps";
import { type ArticleSeed, SEED } from "./seedTypes";
import { useRemote } from "./useRemote";
import { useSeed } from "./useSeed";

/** The article id is the last part of the slug: `/<alias>/<words>-<id>`. */
const articleIdOf = (slug: string): string => slug.split("-").pop() ?? slug;

/** The article, its author and its related lists: from the server render, or from the article and recommendations endpoints. */
const fetchArticle = async (alias: string, articleId: string): Promise<ArticleSeed> => {
  const [{ article, meta, author }, related] = await Promise.all([getArticle(alias, articleId), getArticleRecommendations(alias, articleId)]);
  return {
    meta,
    article,
    author,
    authors: { ...related.authors, [author.alias]: author },
    moreFromAuthor: related.moreFromAuthor,
    youMayAlsoLike: related.youMayAlsoLike,
  };
};

/** Marks the article read once the visitor has been on it for three minutes and read 80 % of it; signed-out visitors have no reads. */
const useReadReport = (alias: string, articleId: string): void => {
  const signedIn = useIsSignedIn();
  const { markRead } = useReadArticles();
  useAutoRead(`${alias}#${articleId}`, ".disclaimer-box", signedIn, () => {
    markRead(alias, articleId).catch((error: unknown) => console.error("Failed to mark the article as read", error));
  });
};

/** Whether the summary is open, kept in the `summary_expanded` setting (the browser's own when signed out). */
const useSummaryToggle = (): [boolean, () => void] => {
  const { settings, setSetting } = useSettings();
  const expanded = settings.summary_expanded === "on";
  return [expanded, () => void setSetting(SettingKey.SUMMARY_EXPANDED, expanded ? "off" : "on")];
};

const ArticleView = ({ data }: { data: ArticleSeed }) => {
  const { meta, article, author } = data;
  const [expanded, toggleSummary] = useSummaryToggle();
  useReadReport(meta.authorAlias, meta.id);
  useHeaderActions(() => <HeaderControls meta={meta} />, [meta.id]);
  useEffect(() => {
    document.title = titleOf(`${meta.title} by ${author.alias}`);
  }, [meta.id]);

  return (
    <article id="article">
      <ScrollProgress targetSelector=".disclaimer-box" visible={true} />
      <div class="bg-neutral-100 dark:bg-neutral-900 text-gray-900 dark:text-gray-100 rounded-lg">
        <ArticleHero meta={meta} />
        {meta.image.attributionHtml && <Attribution html={meta.image.attributionHtml} />}
        <div class="p-4 text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-800 rounded-b-lg">
          <ByLine meta={meta} author={author} />
          <Summary items={article.summary} expanded={expanded} onToggle={toggleSummary} />
          <ArticleBody article={article} />
        </div>
      </div>
      <ArticleAside meta={meta} author={author} authors={data.authors} moreFromAuthor={data.moreFromAuthor} youMayAlsoLike={data.youMayAlsoLike} />
    </article>
  );
};

/** `/:alias/:slug`: an article. */
export const ArticlePage = ({ alias = "", slug = "" }: PageProps) => {
  const articleId = articleIdOf(slug);
  const seed = useSeed<ArticleSeed>(SEED.article, (found) => found.meta.authorAlias === alias && found.meta.id === articleId);
  const article = useRemote<ArticleSeed>(seed, `${alias}/${slug}`, () => fetchArticle(alias, articleId));

  if (article.status === "missing") {
    return <NotFoundPage />;
  }
  return article.status === "ready" ? <ArticleView key={article.data.meta.id} data={article.data} /> : <div class="min-h-screen" />;
};
